import { userMembershipUpdateSchema } from "@gethere/common/yup/Membership";
import Input from "@gethere/web/components/Input";
import LoadableMarkdown from "@gethere/web/components/LoadableMarkdown";
import { MobileInput } from "@gethere/web/components/MobileField";
import { Button } from "@gethere/web/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@gethere/web/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@gethere/web/components/ui/dialog";
import { Label } from "@gethere/web/components/ui/label";
import { Switch } from "@gethere/web/components/ui/switch";
import client from "@gethere/web/utils/client";
import openNewTab from "@gethere/web/utils/openNewTab";
import useAsync from "@gethere/web/utils/useAsync";
import { useToggle } from "@gethere/web/hooks";
import { useFormik } from "formik";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import { saveAs } from "file-saver";
import { cn } from "@gethere/web/utils/cn";

const AppleWallet = ({
  height = 35.016,
  className,
  disabled,
  onClick,
}: {
  height?: number;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    xmlSpace="preserve"
    id="Artwork"
    width={(110.739 / 35.016) * height}
    viewBox="0 0 110.739 35.016"
    height={height}
    x={0}
    y={0}
    className={cn(
      className,
      disabled && "opacity-50",
      onClick && !disabled && "cursor-pointer",
      onClick && "hover:opacity-80",
      onClick && disabled && "cursor-not-allowed"
    )}
    onClick={onClick}
  >
    <style>{".st3{clip-path:url(#SVGID_2_)}.st9{fill:#fff}"}</style>
    <path
      d="M100.211 0H9.533c-.367 0-.729 0-1.094.002-.307.002-.61.008-.919.013-.666.016-1.339.057-2.004.176a6.703 6.703 0 0 0-1.9.627 6.431 6.431 0 0 0-2.797 2.8 6.605 6.605 0 0 0-.625 1.903c-.12.662-.162 1.331-.179 2.001-.009.307-.01.614-.015.921v17.134c.005.31.006.611.015.922.017.67.059 1.34.179 2.002.117.67.314 1.298.625 1.904.303.596.7 1.144 1.178 1.614.473.477 1.02.875 1.618 1.178.609.312 1.231.51 1.901.631.664.119 1.338.158 2.004.177.309.007.613.011.919.011.366.002.728.002 1.095.002h90.678c.359 0 .724 0 1.084-.002.305 0 .617-.004.922-.011.67-.018 1.342-.058 2-.177a6.783 6.783 0 0 0 1.908-.631 6.308 6.308 0 0 0 1.617-1.178 6.389 6.389 0 0 0 1.181-1.614 6.579 6.579 0 0 0 .619-1.904c.123-.662.162-1.332.186-2.002.004-.311.004-.611.004-.922.008-.363.008-.724.008-1.094V9.534c0-.366 0-.729-.008-1.092 0-.307 0-.614-.004-.921-.023-.67-.062-1.34-.186-2.002a6.592 6.592 0 0 0-.619-1.903 6.467 6.467 0 0 0-2.798-2.8 6.743 6.743 0 0 0-1.908-.627c-.658-.119-1.33-.16-2-.177-.305-.005-.617-.011-.922-.013L100.211 0z"
      style={{
        fill: "#a6a6a6",
      }}
    />
    <path d="M100.211.65a94.038 94.038 0 0 1 1.826.012l.164.003c.529.013 1.222.044 1.904.167a6.128 6.128 0 0 1 1.723.565 5.785 5.785 0 0 1 1.455 1.061c.423.42.78.91 1.059 1.452.271.539.454 1.101.56 1.729.117.63.153 1.288.175 1.891.004.304.004.609.004.926.008.358.008.716.008 1.078v14.948c0 .364 0 .721-.008 1.094 0 .308 0 .606-.004.899-.022.617-.058 1.275-.177 1.916a5.946 5.946 0 0 1-.555 1.713 5.731 5.731 0 0 1-1.066 1.456c-.423.427-.91.782-1.449 1.056a6.21 6.21 0 0 1-1.729.571c-.655.119-1.329.151-1.898.167-.3.007-.607.011-.911.011-.358.002-.722.002-1.08.002H9.533c-.365 0-.726 0-1.095-.002-.301 0-.6-.004-.901-.01-.572-.016-1.245-.048-1.906-.167a6.068 6.068 0 0 1-1.722-.571 5.617 5.617 0 0 1-1.457-1.063 5.56 5.56 0 0 1-1.055-1.447 5.96 5.96 0 0 1-.564-1.724c-.124-.681-.155-1.374-.169-1.904a32.066 32.066 0 0 1-.011-.616l-.003-.287V8.453l.004-.299c.002-.204.005-.408.011-.615.013-.528.045-1.221.17-1.907a5.99 5.99 0 0 1 .564-1.721 5.588 5.588 0 0 1 1.058-1.455A5.757 5.757 0 0 1 3.91 1.398 6.014 6.014 0 0 1 5.631.831C6.315.708 7.009.678 7.53.664l.198-.003c.239-.004.476-.008.714-.009C8.806.65 9.168.65 9.533.65h90.678" />
    <defs>
      <path
        id="SVGID_1_"
        d="M33.265 23.054v.265l-.001.381a17.18 17.18 0 0 1-.005.321c-.006.233-.02.468-.061.698a2.355 2.355 0 0 1-.219.664 2.26 2.26 0 0 1-1.64 1.195c-.23.041-.465.055-.698.061a17.18 17.18 0 0 1-.321.005l-.381.001-1.459-.001 1.358.001H10.985l1.358-.001-1.459.001-.381-.001a17.18 17.18 0 0 1-.321-.005 4.665 4.665 0 0 1-.698-.061 2.366 2.366 0 0 1-.664-.219 2.26 2.26 0 0 1-1.195-1.64 4.665 4.665 0 0 1-.061-.698 11.452 11.452 0 0 1-.004-.321l-.001-.381v-12.42 1.25-1.458l.001-.381c.001-.107.002-.214.005-.321.006-.233.02-.468.061-.698.042-.234.111-.452.219-.664a2.26 2.26 0 0 1 1.64-1.195c.23-.041.465-.055.698-.061.107-.003.214-.004.321-.005l.381-.001h19.054a48.388 48.388 0 0 1 .702.006c.233.006.468.02.698.061.234.042.451.111.664.219a2.26 2.26 0 0 1 1.195 1.64c.041.23.055.465.061.698a48.388 48.388 0 0 1 .006.702V23.054z"
      />
    </defs>
    <clipPath id="SVGID_2_">
      <use
        xlinkHref="#SVGID_1_"
        style={{
          overflow: "visible",
        }}
      />
    </clipPath>
    <path
      d="M8.352 8.169h24.099v16.78H8.352z"
      style={{
        clipPath: "url(#SVGID_2_)",
        fill: "#dedbce",
      }}
    />
    <path
      d="M8.63 8.436h23.564v9.997H8.63z"
      style={{
        clipPath: "url(#SVGID_2_)",
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#40a5d9",
      }}
    />
    <g className="st3">
      <image
        xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAHEAAAA6CAYAAACH4bZ/AAAACXBIWXMAAC4jAAAuIwF4pT92AAAA GXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAArlJREFUeNrsnG1vEkEUhXeXBSpU FCW+6zdN/P8/qGk00WhFY6CWsqD3JmfS6bpLoWGXNnlOckJ32NkPPNw7MzT3JglCCKE9KN3zfWh/ +rsPiGn0Ghs1D6/sW0H08czckXNdZ4BsHODavDIXel3dBDOvGAuwuuYj8wO5r/uJyGYBOryF+Vy+ 0Fhtis1rItCBHZvH5qfmx7ruKzJZJ5tZ/wLA3+Yz+ZdgFttATKMIdGDPzW/lFwJ6JIgAbAZkIWAO 75P5RO95hP6pS6vlSOwI1FjwPprfm18pGoHYDsRv5pHGF/IyWh9rIWa6Hpgn5ncC+MH80vzQ3GNN bBTiSmvgSDzm5p9KqXOB/C8a89IxIqRSh/ja/EZROFEU5gBsVGttIjOlz+/mz+YvArnQPbUQO4q0 Y21mngneSA/u6eGo2Wh0FkMtXxOxGEWbyuWmdBrWQ5/wRB5FEcgZsR2FYAogx1rK4v1IGkdjVorE vib45EdaH7sAbE1picVAPIYCW7mpzCrS6VATB5smokZBhk1mTzC7m35oyUrfgFwT+noAafTwEdkp pdGkDuLOE1FrIG/850N224no7ogjAxAREBEQERCBiICIgIiACEQERAREBEQgIiAiICIgAhEBEQER ARGICIgIiAiIQET3RXnN+M4NcdDetPNnn1c8YJ1cb4gTeqhQl9EexPC5ryIeWzUjCgC9nNiL/73Q fxbdB8R2Ic7E4EJMakHmJYCXmjg1fxU4Lzal0LRdiB6B52IwFZPLOpB5ib53bPBGOKca934qoVYc tafQCmUqFmdiU2wDcSZwiR7gnTS6CbWKh9jYLMXjh5jMIojXQMbtMP244SXe3u5kmFw1XSCVHi6l LpVW54rEypSalv4OBf/B1OsfFuQ62qkWdWtiWnFNufc9OzNualqL7hZMhBBCzeqfAAMARUSzZ7uC WKkAAAAASUVORK5CYII="
        width={109}
        height={52}
        style={{
          overflow: "visible",
          opacity: 0.17,
        }}
        transform="matrix(.24 0 0 .24 7.444 9.108)"
      />
      <path
        d="M32.194 12.688v-.242l-.003-.204a2.988 2.988 0 0 0-.039-.443 1.499 1.499 0 0 0-.139-.421 1.414 1.414 0 0 0-1.04-.759 2.988 2.988 0 0 0-.443-.039 6.939 6.939 0 0 0-.204-.003H10.498l-.204.003a2.988 2.988 0 0 0-.443.039c-.148.027-.286.07-.421.139a1.394 1.394 0 0 0-.619.62 1.499 1.499 0 0 0-.139.421 2.988 2.988 0 0 0-.039.443 6.939 6.939 0 0 0-.003.204v1.167-.727 7.689h23.564v-7.887z"
        style={{
          fillRule: "evenodd",
          clipRule: "evenodd",
          fill: "#ffb003",
        }}
      />
    </g>
    <g className="st3">
      <image
        xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAHEAAAA6CAYAAACH4bZ/AAAACXBIWXMAAC4jAAAuIwF4pT92AAAA GXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAqFJREFUeNrsnNtu00AURW3HdkJK UxAX8YAqUN/4//9BCCQQlIBQUtrGSZkj7ZGmxpNLZU9baS1pK7Xr8UNWzxkn1UyWAQBAD+Q9XQP9 c9OHxLwjkEZeOwdL9MJGSqnXApFJBG5c1i6NXte7ZJYRgXZ+4jJVxjqHyOEFmrwrlwvlUueiLbZL Yilxz1xeKrNAJPPkcPOfF/jH5Vz5LZnNPhJ9C51I4FuXdy6nEjnV9QgcTmQjYSbvs8tH/c4q9G+s rXZJNFmvXN67fHA5c3mNxKQSv6v7ZapMyyqYHzsl5prvKpcjVd6pBJ7peCLJSBxO4lpz4Ew+li6/ 1FKXEvlfNZaR+fC5qu+NqtLaa60bw3BY23yi99na5w+XLy5fJfJK19xsq8RaEk8kbqbjWlVKFQ5f jbm6oX+wfBE8WI5UjdF2OpKspxo0019FFbRRJA6P9+BFWlc8bk1neViNRWvwWBJPgoElApMRftEy Vhc8ltA69kxSdFTiVCKn2wbCoCILFU8tmdW2Yio6vqmp9hkIySpytGs6K+46EJKJ3PkPiOKuA+Hh wOc+JAISAYmARCQCEgGJgEQkAhIBiYBEJAISAYmARCQCEgGJgEQkAhIBiYBEJMJjoYycP3hDHOiN g9/7suMGm+z2hjh+DxXWZaST6N/3deBjr82IvEBbTmyL/22h/yK4DolpJS7k4FJOoiLLlsBrDZy7 fJM4W2zKQtO0Eq0CL+RgLifXMZFly77t2GAb4XzSedtPxa8Vh3T4rVDmcnEuN80+EhcSl+kGtuzb 75pBJaZ9sFnJx085WQQSb4nMg1e/BYrtmHGkNlrRSu+1pa7UVpeqxM6Wmrd+9gv+fdhV8X5FboIn 1SY2J+Ydxyz3fmSfGWOSkPfwZAIAwLD8E2AAOBuy81Gw3f4AAAAASUVORK5CYII="
        width={109}
        height={52}
        style={{
          overflow: "visible",
          opacity: 0.16,
        }}
        transform="matrix(.24 0 0 .24 7.444 11.268)"
      />
      <path
        d="M32.194 14.83v-.242l-.003-.204a2.988 2.988 0 0 0-.039-.443 1.499 1.499 0 0 0-.139-.421 1.414 1.414 0 0 0-1.04-.759 2.988 2.988 0 0 0-.443-.039 6.939 6.939 0 0 0-.204-.003H10.498l-.204.003a2.988 2.988 0 0 0-.443.039c-.148.027-.286.07-.421.139a1.394 1.394 0 0 0-.619.62 1.499 1.499 0 0 0-.139.421 2.988 2.988 0 0 0-.039.443 6.939 6.939 0 0 0-.003.204v1.167-.727 7.689h23.564V14.83z"
        style={{
          fillRule: "evenodd",
          clipRule: "evenodd",
          fill: "#40c740",
        }}
      />
    </g>
    <g className="st3">
      <image
        xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAHEAAAA6CAYAAACH4bZ/AAAACXBIWXMAAC4jAAAuIwF4pT92AAAA GXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAvBJREFUeNrsnFtv00AQhX1Ngkkv EYW+ICQET/z//4NASEBbUi5JyMVOmJHOiMF4mzRJnVQ6RzrazdbrB3+and1KO1FEURRFUZQqvscz caBP7aYV3NTfGaKBSlybwokbp3YDuIQreOnGV5vAjO+AZ8BycSbuwF20GSNyLxFYiufiGdo5xhYO 7J0wswBAHe+JC/FTtNbvo98B5IggtwIYAZBCm4hH4jH6E9efAmpwiW2CmAHSufhC/Fw8EJ+JT9H2 EZEpAe4EskIEKsAf4p9ob8XX4hvxd8AsN4FoS2gPAF+KX4tfiV9gTCGeuEhkXtw9H1ok/gJEhXYl /ih+j2f1ud+hZbUJYoHoU4DvxG/FlwD4BJCZE/efE6cApSC/IlgiROrM5cggRNt95sh7F4hABfgG vwu3yUkJcK8gK7eZmSBdxciLt4jOMf7+XzRmgXw4wBJ6CYDnyIGJg0eA+wNpu/8l2hhRqXnxk/gz QM7c0SMYiR1APHM5sABALp8P+w+XFRhEWA1tY/kMHGwjuai/IKnlww5C+dTlwLwWgdTDwfQcDOQA +bEXSmOJ66eg3Uck2kRG4GFAdrEKngBoJ3Ska4rEouFAT4Dtgkxcnuy6DWW8DqJtbPJNJlKtRWS6 7jSQbDuRag3k2tNAsu1E6niU8BMQIkWIFCFShEiIFCFShEgRIiFShEgRIkWIhEgRIkWIFCESIkWI FCFShEiIFCFShEgRIiFSj0RZYHzVYKod3fvbZw0v8BWOyuhvDRXey2gPon33yvHYqBiRAdTrxFrJ QS/6j9xzhNguRCtONAWTIMisBnCOiUPxF4DTy6a8aNouRI3ACRgMwWQeApnV6GvFBq1i9AHjWhTH 7opT7alCBA7B4gZsyk0gjgAuwgv02nce8a7iITY2C/D4BiYjB/EfkL5+qZVA0YoZVpQv51J60CXV ihONEYmNS2pc69uFfzNrtx0W5NLtVMtQTowbfvO69yM7M4YgEd7xwaQo6qj1R4ABAM5x0/sxgGqp AAAAAElFTkSuQmCC"
        width={109}
        height={52}
        style={{
          overflow: "visible",
          opacity: 0.17,
        }}
        transform="matrix(.24 0 0 .24 7.444 13.428)"
      />
      <path
        d="M32.194 16.972v-.242l-.003-.204a2.988 2.988 0 0 0-.039-.443 1.499 1.499 0 0 0-.139-.421 1.414 1.414 0 0 0-1.04-.759 2.988 2.988 0 0 0-.443-.039 6.939 6.939 0 0 0-.204-.003H10.498l-.204.003a2.988 2.988 0 0 0-.443.039c-.148.027-.286.07-.421.139a1.394 1.394 0 0 0-.619.62 1.499 1.499 0 0 0-.139.421 2.988 2.988 0 0 0-.039.443 6.939 6.939 0 0 0-.003.204v1.167-.727 7.689h23.564v-7.887z"
        style={{
          fillRule: "evenodd",
          clipRule: "evenodd",
          fill: "#f26d5f",
        }}
      />
    </g>
    <path
      d="M7.202 7.008v11.068H8.63v-7.772l.003-.204c.004-.148.013-.297.039-.443.027-.148.07-.286.139-.421a1.414 1.414 0 0 1 1.04-.757c.146-.026.295-.035.443-.039l.204-.003H30.326l.204.003c.148.004.297.013.443.039.148.027.286.07.421.139a1.394 1.394 0 0 1 .619.62c.069.135.112.273.139.421.026.146.035.295.039.443l.003.204v7.772h1.428V7.008H7.202z"
      style={{
        clipPath: "url(#SVGID_2_)",
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#d9d6cc",
      }}
    />
    <g className="st3">
      <image
        xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAH0AAAA6CAYAAACd3VbxAAAACXBIWXMAAC4jAAAuIwF4pT92AAAA GXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAABMZJREFUeNrsnQtz0kAUhTfJ8iog 2LdI7VhH6/j//5DjjLa1D22hBQrEXT13uOwECBSTEO6ZObNIKWbvt3v3EdgqJRKJRCKRKA/y1vSa JBTmLK6pxc1bcOGu07roMOJxlhqC58RMpRwzHqvQjZs3pxK+sWb2U6iEvdAxPILpuTCiQaQF2mdl APspxYzHbchMMYu8ILr4onHFuGq8Y1xAZbyEW6q94IFxH+WQNQD+eJwQfKo/B6wRnwLiVkKpU+jx IeLxbPxo3DV+Quz+gtczKqUBfN/40HjPuIZKJVEJAjfCxdLF27LHGsET3EMlR/8ZPNU9QCzKiBN5 B52khsdFvDapcZ46io1Fx/jG+Mr4GsCfF0GvAfhH41PjXVQySLjF9lGBe5SPAP1g/Mv4DmUH8KdS 2ZqB05BXRnyaiIstG8Z1lK/w81LC2ZE6io3DrfFXPNdDHG1svDg93QL/bPwGrTdIsNWOWU8n4GQL +qfxd/gSzz2iRa8TPAEvIAYW8pFxCz4E/Doz9fQkx3XqKDYGP/DcHeJEQ43SC8b0KipzbPwWrVcn nKqGgEhpfYCefo/U9Q29i2ehdYJ3gduhrm383vjM+ATQm/h5JcUxneLVweMrMJxqfDpGRctsnKon CJ2DHzkm8AfGr1kq9dnvdgH+JWO8xzIfAX9n/Mn4HNBbuIYdBlqzjJj0JG6Ix1WwK7jZRsdo4T6b oeqEoVNFdMQSroaKVdjYqZzl3Zgt81aFHiB4TfRwC/wLyjYagru6SWtvI2RcZy4b9RKtPa3KeE6F AtYQNFs28dl+n83mexGbOsuk9RImZsfo2ecAfgrg1YjelOamzEJOWm2OvAUVi5r0DSI2dJYdx6uA a8fuD/CJs4z1Vba2Xudqk6AvAkPLuy6Wc138e8TWrnHAu++7izR+BrfxXHUTgW869Hk9kvf0sTOx G89I994SPXxjgecB+qyxt89SO80DbiJSfhix01Z0lmZ8pn6M/6O0qcDzAj1qls334gNAqmCXqst2 p3ij0HgdpfSWM3Fr473LKeyyCfQ5kzxaT5MoA9CW6SV2qB6wzqd1fAGNoo419xHW42fYhOFLszRu ogj0mDtnivX+BnbNLtS/mw+32NHr4XVlpG3bw/fZDmQLDaDJ1uL+JgPPG/SoiV3AxvoDwL5B+Rtj vALQBqDvsbKJLFFW6X2mQKAvCd5nqbuBXsvv2PGeXlOTu2P0GYJyxE6bEujZH+P57dC6muzY9Z0x vaQmN0oKanorU+UFeJ6hc0j8/kER8N1P2/BPwQQsW+QK9jZAd+HTnj2l/Kh1uspTGt9m6G4DcG/g qLz2aIE+OwNsnXwlEugigS4S6CKBLhLoIoEuEugigS4S6CKBLhLoIoEuEugigS4S6CKBLhLo26g4 H5eaefKgKHOKxUoveAN+WiOdPGjlSXwzC504jRi/MA50Ak6nOtlvenbY7wj0bEPvgBkdqjgFXs8B PsAv2u99XQB0kufIiVaDTufIXajJV7MHHLye01rsV3ntNzzp1EF7JlmSJ0aKVpN7YuQ1WA7jQO8A tMIbJHk2rOhlEzn3bNgOgx5m+RRo0ctS/MxToLN+3rtodfBLnffOwWfhLzuI1rxm36S/4SJaHb5o 2/VHgAEAKq1w/7wLXkMAAAAASUVORK5CYII="
        width={121}
        height={53}
        style={{
          overflow: "visible",
          opacity: 0.14,
        }}
        transform="matrix(.24 0 0 .24 6.004 15.348)"
      />
      <path
        d="m26.985 17.005-.46.001c-.129.001-.258.002-.387.006a5.678 5.678 0 0 0-.843.074 2.866 2.866 0 0 0-.801.264c-.033.017-.738.337-1.372 1.323-.481.749-1.417 1.543-2.727 1.543s-2.246-.794-2.727-1.543c-.667-1.039-1.428-1.351-1.373-1.323a2.834 2.834 0 0 0-.801-.264 5.547 5.547 0 0 0-.843-.074 24.97 24.97 0 0 0-.387-.006l-.46-.001h-6.6v9.996h26.42v-9.996h-6.639z"
        style={{
          fillRule: "evenodd",
          clipRule: "evenodd",
          fill: "#dedbce",
        }}
      />
    </g>
    <path
      d="M41.23 17.661h1.351l2.926 8.057h-1.312l-.737-2.177h-3.105l-.743 2.177h-1.312l2.932-8.057zm-.542 4.875h2.429l-1.2-3.54h-.022l-1.207 3.54zM46.28 19.844h1.206v1.011h.028c.352-.698 1-1.122 1.837-1.122 1.497 0 2.457 1.173 2.457 3.048v.006c0 1.871-.966 3.048-2.44 3.048-.832 0-1.508-.424-1.854-1.105h-.028v2.943H46.28v-7.829zm4.3 2.943v-.006c0-1.251-.598-2.015-1.541-2.015-.916 0-1.558.793-1.558 2.015v.006c0 1.218.648 2.01 1.558 2.01.948 0 1.541-.771 1.541-2.01zM52.999 19.844h1.206v1.011h.028c.352-.698.999-1.122 1.837-1.122 1.497 0 2.457 1.173 2.457 3.048v.006c0 1.871-.966 3.048-2.44 3.048-.832 0-1.508-.424-1.854-1.105h-.028v2.943h-1.206v-7.829zm4.299 2.943v-.006c0-1.251-.598-2.015-1.541-2.015-.916 0-1.558.793-1.558 2.015v.006c0 1.218.648 2.01 1.558 2.01.949 0 1.541-.771 1.541-2.01zM59.59 17.661h1.207v8.057H59.59v-8.057zM61.97 22.803v-.006c0-1.837 1.061-3.065 2.709-3.065s2.658 1.183 2.658 2.948v.408h-4.159c.022 1.111.631 1.758 1.591 1.758.715 0 1.19-.368 1.341-.809l.017-.045h1.144l-.011.062c-.189.932-1.095 1.781-2.518 1.781-1.727 0-2.772-1.178-2.772-3.032zm1.224-.569h2.948c-.101-1.016-.67-1.513-1.457-1.513-.783 0-1.386.53-1.491 1.513zM71.171 17.661h1.301l1.503 6.315h.022l1.703-6.315h1.183l1.703 6.315h.028l1.502-6.315h1.302l-2.178 8.057h-1.2l-1.731-6.075h-.029l-1.737 6.075h-1.201l-2.171-8.057zM81.761 24.048v-.011c0-1.022.794-1.647 2.184-1.731l1.596-.095v-.441c0-.647-.419-1.039-1.167-1.039-.698 0-1.128.33-1.228.799l-.012.051h-1.138l.005-.062c.084-1.011.961-1.787 2.407-1.787 1.435 0 2.345.76 2.345 1.937v4.048h-1.212v-.927h-.022c-.341.631-1.021 1.033-1.798 1.033-1.172.001-1.96-.719-1.96-1.775zm2.289.811c.848 0 1.491-.575 1.491-1.341v-.458l-1.435.09c-.725.044-1.128.362-1.128.859v.012c0 .513.425.838 1.072.838zM88.074 17.661h1.206v8.057h-1.206v-8.057zM90.979 17.661h1.206v8.057h-1.206v-8.057zM93.245 22.803v-.006c0-1.837 1.06-3.065 2.708-3.065 1.648 0 2.659 1.183 2.659 2.948v.408h-4.16c.022 1.111.631 1.758 1.592 1.758.715 0 1.189-.368 1.34-.809l.018-.045h1.144l-.011.062c-.19.932-1.095 1.781-2.519 1.781-1.727 0-2.771-1.178-2.771-3.032zm1.223-.569h2.949c-.101-1.016-.67-1.513-1.458-1.513-.782 0-1.385.53-1.491 1.513zM100.142 24.171v-3.367h-.844v-.961h.844v-1.53h1.233v1.53h1.1v.961h-1.1v3.294c0 .598.269.776.776.776.129 0 .234-.012.324-.022v.932c-.14.022-.368.05-.614.05-1.161.001-1.719-.49-1.719-1.663zM42.193 12.483h-2.408l-.608 1.733h-.793l2.232-6.054h.747l2.232 6.054h-.793l-.609-1.733zm-2.185-.642h1.964l-.949-2.702h-.067l-.948 2.702zM44.074 11.954c0-1.418.751-2.341 1.901-2.341.629 0 1.162.298 1.418.793h.063V7.897h.722v6.318h-.688v-.722h-.067c-.285.503-.822.801-1.448.801-1.158.001-1.901-.918-1.901-2.34zm.747 0c0 1.057.495 1.691 1.322 1.691.822 0 1.33-.646 1.33-1.691 0-1.036-.512-1.691-1.33-1.691-.823 0-1.322.638-1.322 1.691zM49.342 11.954c0-1.418.751-2.341 1.901-2.341.629 0 1.162.298 1.418.793h.063V7.897h.722v6.318h-.688v-.722h-.068c-.285.503-.822.801-1.448.801-1.158.001-1.9-.918-1.9-2.34zm.746 0c0 1.057.495 1.691 1.322 1.691.822 0 1.33-.646 1.33-1.691 0-1.036-.512-1.691-1.33-1.691-.823 0-1.322.638-1.322 1.691zM58.274 8.522v1.171h1.008v.604h-1.008v2.56c0 .533.202.759.672.759.13 0 .201-.004.336-.017v.608a2.447 2.447 0 0 1-.424.042c-.931 0-1.305-.344-1.305-1.208v-2.744h-.729v-.604h.729V8.522h.721zM60.072 11.954c0-1.456.798-2.341 2.086-2.341s2.085.885 2.085 2.341c0 1.451-.797 2.341-2.085 2.341s-2.086-.89-2.086-2.341zm3.424 0c0-1.074-.483-1.691-1.339-1.691s-1.339.617-1.339 1.691c0 1.07.482 1.691 1.339 1.691.856 0 1.339-.621 1.339-1.691z"
      className="st9"
    />
  </svg>
);

const GoogleWallet = ({
  height = 55,
  className,
  onClick,
  disabled,
}: {
  height?: number;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={(199 / 55) * height}
    height={height}
    fill="none"
    viewBox="0 0 199 55"
    onClick={onClick}
    className={cn(
      className,
      disabled && "opacity-50",
      onClick && !disabled && "cursor-pointer",
      onClick && "hover:opacity-80",
      onClick && disabled && "cursor-not-allowed"
    )}
  >
    <rect width={198} height={54} x={0.5} y={0.5} fill="#1F1F1F" rx={27} />
    <path
      fill="#34A853"
      d="M57 23.791H21v-5.645c0-3.065 2.642-5.646 5.78-5.646h24.44c3.138 0 5.78 2.58 5.78 5.646v5.645Z"
    />
    <path
      fill="#FBBC04"
      d="M57 29H21v-6c0-3.257 2.642-6 5.78-6h24.44c3.138 0 5.78 2.743 5.78 6v6Z"
    />
    <path
      fill="#EA4335"
      d="M57 34H21v-6c0-3.257 2.642-6 5.78-6h24.44c3.138 0 5.78 2.743 5.78 6v6Z"
    />
    <path
      fill="url(#a)"
      d="m21 25.24 22.85 5.163c2.63.645 5.588 0 7.725-1.613L57 24.918v12.098c0 3.064-2.63 5.484-5.753 5.484H26.753C23.63 42.5 21 40.08 21 37.016V25.24Z"
    />
    <path
      fill="#fff"
      d="m69.195 21.5 3.51-9.308h1.625l3.523 9.308H76.28l-.858-2.392h-3.796l-.858 2.392h-1.573Zm3.939-6.565-1.027 2.847h2.834l-1.027-2.847-.351-1.066h-.078l-.351 1.066Zm8.38 6.773a3.07 3.07 0 0 1-1.625-.442 3.317 3.317 0 0 1-1.144-1.248c-.277-.537-.416-1.148-.416-1.833s.139-1.291.416-1.82a3.317 3.317 0 0 1 1.144-1.248 3.004 3.004 0 0 1 1.625-.455c.512 0 .95.113 1.313.338.373.225.646.481.82.767h.077l-.078-.923v-2.652h1.391V21.5h-1.313v-.884h-.078c-.173.286-.446.542-.819.767-.364.217-.801.325-1.313.325Zm.208-1.287c.347 0 .672-.091.975-.273.312-.182.56-.438.741-.767.191-.338.286-.737.286-1.196 0-.46-.095-.854-.286-1.183a1.95 1.95 0 0 0-.74-.78 1.863 1.863 0 0 0-.976-.273c-.346 0-.671.091-.975.273-.303.182-.55.442-.74.78-.191.33-.287.724-.287 1.183 0 .46.096.858.286 1.196.191.33.438.585.741.767.304.182.629.273.975.273Zm7.74 1.287a3.07 3.07 0 0 1-1.625-.442 3.316 3.316 0 0 1-1.144-1.248c-.278-.537-.416-1.148-.416-1.833s.138-1.291.416-1.82a3.316 3.316 0 0 1 1.144-1.248 3.004 3.004 0 0 1 1.625-.455c.51 0 .949.113 1.313.338.372.225.645.481.819.767h.078l-.078-.923v-2.652h1.39V21.5h-1.312v-.884h-.078c-.174.286-.447.542-.82.767-.363.217-.801.325-1.312.325Zm.208-1.287c.346 0 .671-.091.975-.273.312-.182.559-.438.74-.767.191-.338.287-.737.287-1.196 0-.46-.096-.854-.286-1.183a1.95 1.95 0 0 0-.741-.78 1.863 1.863 0 0 0-.975-.273c-.347 0-.672.091-.975.273-.304.182-.55.442-.741.78-.191.33-.286.724-.286 1.183 0 .46.095.858.286 1.196.19.33.437.585.74.767.304.182.629.273.976.273Zm8.704-.845v-3.484h-1.156V14.87h1.156v-1.872h1.404v1.872h1.626v1.222h-1.626v3.185c0 .33.066.58.195.754.139.173.369.26.69.26.164 0 .303-.022.416-.065.121-.043.242-.104.364-.182v1.365c-.148.06-.304.108-.468.143-.165.035-.36.052-.585.052-.616 0-1.106-.178-1.47-.533-.364-.364-.546-.862-.546-1.495Zm7.243 2.132c-.685 0-1.287-.156-1.807-.468a3.341 3.341 0 0 1-1.222-1.261c-.295-.529-.442-1.127-.442-1.794 0-.659.147-1.252.442-1.781a3.316 3.316 0 0 1 1.222-1.274c.52-.312 1.122-.468 1.807-.468.676 0 1.274.156 1.794.468.52.312.927.737 1.222 1.274.294.529.442 1.122.442 1.781 0 .667-.148 1.265-.442 1.794a3.349 3.349 0 0 1-1.222 1.261c-.52.312-1.118.468-1.794.468Zm0-1.287c.364 0 .702-.087 1.014-.26a2.07 2.07 0 0 0 .754-.767c.199-.338.299-.741.299-1.209 0-.468-.1-.867-.299-1.196a1.948 1.948 0 0 0-.754-.767 2.046 2.046 0 0 0-2.041 0 2.033 2.033 0 0 0-.767.767c-.191.33-.286.728-.286 1.196 0 .468.095.871.286 1.209.199.33.455.585.767.767.32.173.663.26 1.027.26ZM76.14 41.772a6.41 6.41 0 0 1-2.465-.476 6.52 6.52 0 0 1-2.023-1.326 6.454 6.454 0 0 1-1.394-2.023 6.47 6.47 0 0 1-.493-2.533c0-.907.164-1.745.493-2.516a6.338 6.338 0 0 1 1.377-2.023 6.322 6.322 0 0 1 2.04-1.343 6.41 6.41 0 0 1 2.465-.476c.93 0 1.785.164 2.567.493a5.446 5.446 0 0 1 1.989 1.377l-1.292 1.275a4.024 4.024 0 0 0-1.428-1.02c-.544-.238-1.156-.357-1.836-.357a4.44 4.44 0 0 0-2.227.578A4.37 4.37 0 0 0 72.264 33c-.408.68-.612 1.485-.612 2.414 0 .93.21 1.74.629 2.431.42.68.969 1.213 1.649 1.598.68.374 1.422.561 2.227.561.737 0 1.405-.136 2.006-.408a3.64 3.64 0 0 0 1.445-1.19c.374-.51.6-1.122.68-1.836h-4.165v-1.649h5.916c.068.306.102.629.102.969v.017c0 1.179-.26 2.21-.782 3.094a5.336 5.336 0 0 1-2.125 2.04c-.907.487-1.938.731-3.094.731Zm11.475 0c-.895 0-1.683-.204-2.363-.612a4.376 4.376 0 0 1-1.598-1.649c-.385-.691-.578-1.473-.578-2.346 0-.861.193-1.638.578-2.329a4.341 4.341 0 0 1 1.598-1.666c.68-.408 1.468-.612 2.363-.612.884 0 1.666.204 2.346.612.68.408 1.213.963 1.598 1.666.385.691.578 1.468.578 2.329 0 .873-.193 1.655-.578 2.346a4.377 4.377 0 0 1-1.598 1.649c-.68.408-1.462.612-2.346.612Zm0-1.683c.476 0 .918-.113 1.326-.34.408-.238.736-.572.986-1.003.26-.442.39-.969.39-1.581s-.13-1.133-.39-1.564a2.546 2.546 0 0 0-.986-1.003 2.586 2.586 0 0 0-1.326-.357c-.476 0-.924.119-1.343.357a2.662 2.662 0 0 0-1.003 1.003c-.25.43-.374.952-.374 1.564 0 .612.124 1.139.374 1.581.26.43.595.765 1.003 1.003.42.227.867.34 1.343.34Zm10.293 1.683c-.895 0-1.683-.204-2.363-.612a4.377 4.377 0 0 1-1.598-1.649c-.386-.691-.578-1.473-.578-2.346 0-.861.192-1.638.578-2.329a4.342 4.342 0 0 1 1.598-1.666c.68-.408 1.468-.612 2.363-.612.884 0 1.666.204 2.346.612a4.34 4.34 0 0 1 1.598 1.666c.385.691.578 1.468.578 2.329 0 .873-.193 1.655-.578 2.346a4.374 4.374 0 0 1-1.598 1.649c-.68.408-1.462.612-2.346.612Zm0-1.683c.476 0 .918-.113 1.326-.34.408-.238.737-.572.986-1.003.261-.442.391-.969.391-1.581s-.13-1.133-.391-1.564a2.547 2.547 0 0 0-.986-1.003 2.586 2.586 0 0 0-1.326-.357c-.476 0-.924.119-1.343.357a2.662 2.662 0 0 0-1.003 1.003c-.25.43-.374.952-.374 1.564 0 .612.124 1.139.374 1.581.26.43.595.765 1.003 1.003.42.227.867.34 1.343.34Zm10.123 5.355c-.76 0-1.417-.125-1.972-.374-.544-.238-.986-.55-1.326-.935a3.584 3.584 0 0 1-.731-1.156l1.7-.714c.181.453.47.822.867 1.105.408.295.895.442 1.462.442.793 0 1.416-.238 1.87-.714.464-.476.697-1.15.697-2.023v-.833h-.102a2.948 2.948 0 0 1-1.139.986c-.476.238-1.02.357-1.632.357a3.804 3.804 0 0 1-2.023-.561 4.183 4.183 0 0 1-1.479-1.564c-.374-.68-.561-1.473-.561-2.38 0-.918.187-1.711.561-2.38.374-.68.867-1.207 1.479-1.581a3.804 3.804 0 0 1 2.023-.561c.612 0 1.156.125 1.632.374.487.25.867.578 1.139.986h.102V32.83h1.751v8.211c0 .94-.187 1.74-.561 2.397a3.633 3.633 0 0 1-1.513 1.496c-.646.34-1.394.51-2.244.51Zm.017-5.525c.453 0 .872-.108 1.258-.323.385-.227.697-.55.935-.969.238-.43.357-.946.357-1.547 0-.623-.119-1.145-.357-1.564a2.413 2.413 0 0 0-.935-.969 2.539 2.539 0 0 0-1.258-.323c-.454 0-.879.113-1.275.34a2.433 2.433 0 0 0-.935.952c-.238.42-.357.94-.357 1.564 0 .612.119 1.133.357 1.564.238.42.549.737.935.952.396.215.821.323 1.275.323Zm6.466 1.581V29.328h1.836V41.5h-1.836Zm7.878.272c-.85 0-1.609-.198-2.278-.595a4.3 4.3 0 0 1-1.581-1.632c-.374-.691-.561-1.479-.561-2.363 0-.827.182-1.592.544-2.295a4.344 4.344 0 0 1 1.513-1.683c.658-.43 1.411-.646 2.261-.646.896 0 1.655.193 2.278.578a3.738 3.738 0 0 1 1.445 1.581c.329.669.493 1.422.493 2.261 0 .125-.005.238-.017.34 0 .102-.005.181-.017.238h-6.698c.091.861.391 1.507.901 1.938.522.43 1.111.646 1.768.646.59 0 1.077-.13 1.462-.391.386-.272.692-.606.918-1.003l1.513.731a4.692 4.692 0 0 1-1.53 1.666c-.646.42-1.45.629-2.414.629Zm-.085-7.65c-.612 0-1.133.187-1.564.561-.43.374-.719.873-.867 1.496h4.811a2.317 2.317 0 0 0-.289-.918 2.104 2.104 0 0 0-.782-.799c-.34-.227-.776-.34-1.309-.34Zm11.672 7.378-3.264-12.172h2.074l1.938 7.905.204 1.054h.102l.255-1.054 2.448-7.905h1.87l2.346 7.905.255 1.037h.102l.204-1.037 1.938-7.905h2.057L143.295 41.5h-1.972l-2.329-8.075-.272-1.139h-.102l-.289 1.139L135.9 41.5h-1.921Zm15.943.272c-.623 0-1.179-.119-1.666-.357a2.807 2.807 0 0 1-1.139-1.037 2.828 2.828 0 0 1-.408-1.513c0-.612.159-1.133.476-1.564a3.06 3.06 0 0 1 1.309-1.003 4.641 4.641 0 0 1 1.802-.34c.544 0 1.02.051 1.428.153.419.102.737.21.952.323v-.459c0-.567-.204-1.02-.612-1.36-.408-.34-.935-.51-1.581-.51-.442 0-.861.102-1.258.306a2.434 2.434 0 0 0-.952.799l-1.258-.969c.374-.521.867-.93 1.479-1.224a4.564 4.564 0 0 1 2.04-.459c1.258 0 2.227.312 2.907.935.68.612 1.02 1.473 1.02 2.584V41.5h-1.785v-1.071h-.102c-.227.351-.567.663-1.02.935-.453.272-.997.408-1.632.408Zm.323-1.496c.476 0 .895-.113 1.258-.34a2.44 2.44 0 0 0 .85-.884c.215-.374.323-.776.323-1.207a3.795 3.795 0 0 0-.918-.357 4.003 4.003 0 0 0-1.122-.153c-.748 0-1.281.153-1.598.459a1.428 1.428 0 0 0-.476 1.088c0 .408.153.742.459 1.003.306.26.714.391 1.224.391Zm6.254 1.224V29.328h1.836V41.5h-1.836Zm4.018 0V29.328h1.836V41.5h-1.836Zm7.878.272c-.85 0-1.609-.198-2.278-.595a4.3 4.3 0 0 1-1.581-1.632c-.374-.691-.561-1.479-.561-2.363 0-.827.182-1.592.544-2.295a4.344 4.344 0 0 1 1.513-1.683c.658-.43 1.411-.646 2.261-.646.896 0 1.655.193 2.278.578a3.738 3.738 0 0 1 1.445 1.581c.329.669.493 1.422.493 2.261 0 .125-.005.238-.017.34 0 .102-.005.181-.017.238h-6.698c.091.861.391 1.507.901 1.938.522.43 1.111.646 1.768.646.59 0 1.077-.13 1.462-.391.386-.272.692-.606.918-1.003l1.513.731a4.692 4.692 0 0 1-1.53 1.666c-.646.42-1.45.629-2.414.629Zm-.085-7.65c-.612 0-1.133.187-1.564.561-.43.374-.719.873-.867 1.496h4.811a2.317 2.317 0 0 0-.289-.918 2.104 2.104 0 0 0-.782-.799c-.34-.227-.776-.34-1.309-.34Zm6.572 4.862v-4.556h-1.513V32.83h1.513v-2.448h1.836v2.448h2.125v1.598h-2.125v4.165c0 .43.085.76.255.986.181.227.481.34.901.34.215 0 .396-.028.544-.085.158-.057.317-.136.476-.238v1.785c-.193.08-.397.142-.612.187a3.77 3.77 0 0 1-.765.068c-.805 0-1.445-.232-1.921-.697-.476-.476-.714-1.128-.714-1.955Z"
    />
    <rect width={198} height={54} x={0.5} y={0.5} stroke="#747775" rx={27} />
    <defs>
      <linearGradient
        id="a"
        x1={37.284}
        x2={18.782}
        y1={34.045}
        y2={55.723}
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#4285F4" />
        <stop offset={1} stopColor="#1B74E8" />
      </linearGradient>
    </defs>
  </svg>
);

export function MembershipComponent({ businessId }: { businessId: string }) {
  const intl = useIntl();

  const { value, successed, failed, error, status, loading } = useAsync(
    () => client.api.me.membership(businessId),
    { immediate: true }
  );

  const formik = useFormik({
    initialValues: {
      email: "",
      name: "",
      mobile: "",
    },
    validationSchema: userMembershipUpdateSchema,
    onSubmit: async (values) => {},
  });

  useEffect(() => {
    if (successed) {
      const { membership } = value?.data;
      formik.setValues({
        email: membership.email,
        name: membership.name as any,
        mobile: membership.mobile,
      });
    }
  }, [successed]);

  const terms = useAsync(async (businessId: string) =>
    client.api.members.policy(businessId)
  );

  const googleWallet = useAsync(async (businessId: string) => {
    const response = await client.api.me.membershipGooglePass(businessId);
    const url = response.data.url;
    openNewTab(url);
    return url;
  });

  const handleAppleWalletDownload = (binary: any) => {
    try {
      const blob = new Blob([binary], {
        type: "application/vnd.apple.pkpass",
      });

      saveAs(blob, value?.data?.membership?.id + ".pkpass");
    } catch (error) {
      console.error("Error downloading Apple Wallet pass", error);
    }
  };

  const appleWallet = useAsync(
    async (businessId: string) => {
      const response = await client.api.me.membershipApplePass(businessId);
      const result = response.data;
      return result;
    },
    {},
    [handleAppleWalletDownload]
  );

  useEffect(() => {
    if (appleWallet.successed) {
      handleAppleWalletDownload(appleWallet.value);
    }
  }, [appleWallet.successed]);

  const dialog = useToggle(false);

  if (status === "idle" || loading) return null;

  if (failed || !successed) {
    return <div>Error: {error.message}</div>;
  }

  const { membership, business, rewards } = value.data;

  return (
    <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8 grid grid-cols-1 gap-4">
      <header>
        <h1 className="text-3xl font-bold">{business?.membership?.name}</h1>
      </header>
      <Card>
        <CardHeader>
          <CardTitle>Membership Details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-gray-500 dark:text-gray-400 mb-1">Visits</p>
            <p className="font-medium">{membership?.orders}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400 mb-1">Points</p>
            <p className="font-medium">
              {intl.formatNumber(membership?.points)}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400 mb-1">Joined Date</p>
            <p className="font-medium">
              {intl.formatDate(membership?.createdAt)}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">
              Terms and Conditions
            </p>
            <span
              className="font-medium hover:underline cursor-pointer"
              onClick={() => {
                dialog.toggle();
                terms.execute(businessId);
              }}
            >
              View
            </span>
          </div>
        </CardContent>
      </Card>
      <div className="flex flex-row gap-5 items-center justify-evenly">
        <AppleWallet
          height={42}
          disabled={appleWallet.loading}
          onClick={async () => {
            if (appleWallet.loading) {
              return;
            }

            if (appleWallet.failed || appleWallet.status === "idle") {
              appleWallet.execute(businessId);
              return;
            }

            if (appleWallet.value) handleAppleWalletDownload(appleWallet.value);
          }}
        />
        <GoogleWallet
          height={42}
          disabled={googleWallet.loading}
          onClick={async () => {
            if (googleWallet.loading) {
              return;
            }

            if (googleWallet.failed || googleWallet.status === "idle") {
              googleWallet.execute(businessId);
              return;
            }

            if (googleWallet.value) openNewTab(googleWallet.value);
          }}
        />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Your Information</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3">
          <div className="grid grid-cols-2 gap-5">
            <Input
              label="First Name"
              name="name.first"
              value={formik.values.name?.["first"]}
              onChange={formik.handleChange}
              error={
                formik.touched.name?.["first"] && formik.errors?.name?.["first"]
              }
              disabled={formik.isSubmitting}
              onBlur={formik.handleBlur}
              className="w-full"
            />
            <Input
              className="w-full"
              label="Last Name"
              name="name.last"
              value={formik.values.name?.["last"]}
              onChange={formik.handleChange}
              error={
                formik.touched.name?.["last"] && formik.errors?.name?.["last"]
              }
              disabled={formik.isSubmitting}
              onBlur={formik.handleBlur}
            />
          </div>

          <div>
            <MobileInput
              name="mobile"
              value={formik.values.mobile}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.mobile && formik.errors.mobile}
              label="Mobile Number"
            />
          </div>
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && formik.errors.email}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Marketing Preferences</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between">
          <p>Receive marketing information</p>
          <Switch />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Cancel Membership</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            If you wish to stop your membership, click the button below. Please
            note that this action is irreversible and you will lose all
            accumulated points and benefits.
          </p>
          <Button variant="destructive">Cancel Membership</Button>
        </CardContent>
      </Card>
      <Dialog open={dialog.value} onOpenChange={dialog.toggle}>
        <DialogContent className="max-h-[90%] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Terms & Conditions</DialogTitle>
          </DialogHeader>
          {terms.successed ? (
            <LoadableMarkdown markdown={terms.value?.data?.content} />
          ) : terms.loading ? (
            "Loading..."
          ) : (
            terms.error?.message
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}

export function MeMembership() {
  const { businessId } = useParams<{ businessId: string }>();
  return <MembershipComponent businessId={businessId} />;
}
